import React, { JSX, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { MERCH_PRICE_DOWN_COLOR, MERCH_PRICE_UP_COLOR, MerchChangeAnimation } from 'lib/merchStyle'
import { MerchTickerStyleDraft } from 'lib/merchTickerConfig'
import {
  MerchTweenHandle,
  formatMerchTweenPct,
  merchTweenMoney,
  parseMerchPctLabel,
  startMerchTween
} from 'lib/merchTween'
import { chatFontFamily } from 'lib/chatFonts'

export const MERCH_TICKER_MARQUEE_WIDTH = 1920
export const MERCH_TICKER_MARQUEE_HEIGHT = 105

const TOP_BAR_PX = 50
const GAP_PX = 25
const BOTTOM_TEXT_PX = 20
const TOP_FONT_PX = 43
const TOP_INK_SHIFT_PX = -2
const BOTTOM_FONT_PX = 20
const BOTTOM_INK_SHIFT_PX = -1
const ARROW_W_PX = 25.5
const ARROW_H_PX = 22.5
const VCR_ARROW_FONT_PX = 37.5
const VCR_ARROW_NUDGE_PX = 1.5
const emOf = (px: number, refPx: number): string => `${px}em / ${refPx}`
const VCR_ARROW_HALF_ASCENT_EM = 0.44

export const MARQUEE_SPEED_PX_S = 160
const TOP_SPEED_MULT = 1
const BOTTOM_SPEED_MULT = 1
const REMEASURE_EPSILON_PX = 0.05
const MAX_SEAM_PHASE_MS = 250
const PATCH_WIDTH_EPSILON_PX = 0.05
const WIDTH_RELEASE_MS = 250
const WIDTH_RELEASE_TIMER_MS = WIDTH_RELEASE_MS + 40
const WIDTH_RELEASE_MIN_PX = 0.5
const FLASH_MS = 900
const BLINK_CYCLE_MS = 400
const BLINK_CYCLES = 3
const BLINK_MS = BLINK_CYCLE_MS * BLINK_CYCLES

const MARQUEE_NEUTRAL_COLOR = '#ffffff'

const MARQUEE_BASE_FONT_STACK = '"VCR OSD Mono", monospace'

const marqueeFontStack = (key: string): string =>
  chatFontFamily(key) ?? MARQUEE_BASE_FONT_STACK

export const merchDebugEnabled = (): boolean => {
  try {
    return new URLSearchParams(globalThis.location.search).get('debug') === '1'
  } catch {
    return false
  }
}

export const merchDebugTrace = (entry: Record<string, unknown>): void => {
  if (merchDebugEnabled()) globalThis.console.debug('[merch]', entry)
}

export interface MerchTickerEnvelopeMessage {
  id: string
  label: string
  labelColor: string
  text: string
}

export interface MerchTickerMarqueeProduct {
  productId: string
  title: string
  priceCents: number | null
  trend: string | null
  pctLabel: string | null
}

export interface MerchTickerMarqueeEnvelope {
  products: MerchTickerMarqueeProduct[]
  messages: MerchTickerEnvelopeMessage[]
  tickerStyle: MerchTickerStyleDraft
  deltas: Record<string, number>
}

const hexToRgba = (hex: string, opacity: number): string => {
  let h = hex.startsWith('#') ? hex.slice(1) : hex
  if (h.length === 3 || h.length === 4) h = h.split('').map((c) => c + c).join('')
  const r = Number.parseInt(h.slice(0, 2), 16)
  const g = Number.parseInt(h.slice(2, 4), 16)
  const b = Number.parseInt(h.slice(4, 6), 16)
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return 'rgba(0, 0, 0, 1)'
  const hexA = h.length === 8 ? Number.parseInt(h.slice(6, 8), 16) / 255 : 1
  const a = Math.round(Math.max(0, Math.min(1, hexA * opacity)) * 1000) / 1000
  return `rgba(${r}, ${g}, ${b}, ${a})`
}

const MARQUEE_CSS = `
.merch-marquee { width: var(--mq-wrap, ${MERCH_TICKER_MARQUEE_WIDTH}px); color: var(--mq-text, ${MARQUEE_NEUTRAL_COLOR}); font-family: var(--mq-font, ${MARQUEE_BASE_FONT_STACK}); font-weight: 400; }
.merch-marquee .mq-outer-top { height: var(--mq-top-outer-pad, 0px); background: var(--mq-top-bg, #000000); }
.merch-marquee .mq-outer-bottom { height: var(--mq-bottom-outer-pad, 0px); background: var(--mq-bottom-bg, rgba(0, 0, 0, 1)); }
.merch-marquee .mq-bar { position: relative; overflow: hidden; border: 0 solid var(--mq-border-color, #ffffff); border-top-width: var(--mq-border-w, 0px); border-bottom-width: var(--mq-border-w, 0px); }
.merch-marquee .mq-top { box-sizing: content-box; height: var(--mq-top-lh, ${TOP_BAR_PX}px); padding: var(--mq-top-pad-top, 0px) 0 var(--mq-top-pad-bottom, 0px); background: var(--mq-top-bg, #000000); }
.merch-marquee .mq-gap { height: var(--mq-gap-h, ${GAP_PX}px); background: var(--mq-gap-bg, transparent); }
.merch-marquee .mq-bottom { box-sizing: content-box; min-height: var(--mq-bottom-lh, ${BOTTOM_TEXT_PX}px); padding: var(--mq-pad-top, 5px) 0 var(--mq-pad-bottom, 5px); background: var(--mq-bottom-bg, rgba(0, 0, 0, 1)); }
.merch-marquee .mq-track { display: flex; width: max-content; will-change: transform; }
.merch-marquee .mq-track.mq-anim { animation: mq-scroll var(--mq-dur, 0s) linear var(--mq-delay, 0ms) infinite; }
.merch-marquee .mq-track.mq-rev { animation-direction: reverse; }
@keyframes mq-scroll { from { transform: translateX(0); } to { transform: translateX(var(--mq-shift, 0px)); } }
.merch-marquee .mq-copy { white-space: pre; text-shadow: var(--mq-text-glow, none); }
.merch-marquee .mq-top .mq-copy { font-size: var(--mq-top-font, ${TOP_FONT_PX}px); line-height: var(--mq-top-lh, ${TOP_BAR_PX}px); transform: translateY(${TOP_INK_SHIFT_PX}px); }
.merch-marquee .mq-bottom .mq-copy { font-size: var(--mq-bottom-font, ${BOTTOM_FONT_PX}px); line-height: var(--mq-bottom-lh, ${BOTTOM_TEXT_PX}px); transform: translateY(${BOTTOM_INK_SHIFT_PX}px); }
.merch-marquee .mq-measure { position: absolute; top: 0; left: 0; visibility: hidden; pointer-events: none; }
.merch-marquee .mq-name { text-transform: uppercase; }
.merch-marquee .mq-quote.up { color: ${MERCH_PRICE_UP_COLOR}; }
.merch-marquee .mq-quote.down { color: ${MERCH_PRICE_DOWN_COLOR}; }
.merch-marquee .mq-quote { display: inline-block; }
.merch-marquee.mq-priceslot .mq-price { display: inline-block; min-width: var(--mq-price-slot, 0px); text-align: right; }
.merch-marquee.mq-pctslot .mq-pct { display: inline-block; min-width: var(--mq-pct-slot, 0px); text-align: right; }
.merch-marquee .mq-slotmeasure { position: absolute; top: 0; left: 0; visibility: hidden; pointer-events: none; white-space: pre; font-size: var(--mq-top-font, ${TOP_FONT_PX}px); }
.merch-marquee .mq-slotmeasure span { display: inline-block; }
.merch-marquee .mq-top .mq-div { padding: 0 var(--mq-top-sepgap, 1.5em); }
.merch-marquee .mq-bottom .mq-div { padding: 0 var(--mq-bottom-sepgap, 1.5em); }
.merch-marquee .mq-arrow { display: inline-block; width: calc(${emOf(ARROW_W_PX, TOP_FONT_PX)}); height: calc(${emOf(ARROW_H_PX, TOP_FONT_PX)}); margin-top: calc(${emOf(-ARROW_H_PX, TOP_FONT_PX)}); background: currentColor; -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 12'%3E%3Ccircle cx='8' cy='2' r='1.7'/%3E%3Ccircle cx='5' cy='6' r='1.7'/%3E%3Ccircle cx='8' cy='6' r='1.7'/%3E%3Ccircle cx='11' cy='6' r='1.7'/%3E%3Ccircle cx='2' cy='10' r='1.7'/%3E%3Ccircle cx='5' cy='10' r='1.7'/%3E%3Ccircle cx='8' cy='10' r='1.7'/%3E%3Ccircle cx='11' cy='10' r='1.7'/%3E%3Ccircle cx='14' cy='10' r='1.7'/%3E%3C/svg%3E") center / contain no-repeat; mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 12'%3E%3Ccircle cx='8' cy='2' r='1.7'/%3E%3Ccircle cx='5' cy='6' r='1.7'/%3E%3Ccircle cx='8' cy='6' r='1.7'/%3E%3Ccircle cx='11' cy='6' r='1.7'/%3E%3Ccircle cx='2' cy='10' r='1.7'/%3E%3Ccircle cx='5' cy='10' r='1.7'/%3E%3Ccircle cx='8' cy='10' r='1.7'/%3E%3Ccircle cx='11' cy='10' r='1.7'/%3E%3Ccircle cx='14' cy='10' r='1.7'/%3E%3C/svg%3E") center / contain no-repeat; filter: var(--mq-arrow-glow, none); }
.merch-marquee .mq-quote.down .mq-arrow { transform: rotate(180deg); }
.merch-marquee .mq-quote.flat .mq-arrow { visibility: hidden; }
.merch-marquee.mq-vcr .mq-arrow { width: auto; height: auto; margin-top: -${VCR_ARROW_HALF_ASCENT_EM}em; background: none; -webkit-mask: none; mask: none; font-size: calc(${emOf(VCR_ARROW_FONT_PX, TOP_FONT_PX)}); line-height: 0; transform: translateY(calc(${emOf(VCR_ARROW_NUDGE_PX, VCR_ARROW_FONT_PX)})); filter: none; }
.merch-marquee.mq-vcr .mq-arrow::before { content: '\\25B2'; }
.merch-marquee.mq-noarrowglow .mq-arrow { text-shadow: none; }
.merch-marquee.mq-vcr .mq-quote.down .mq-arrow { transform: translateY(calc(${emOf(VCR_ARROW_NUDGE_PX, VCR_ARROW_FONT_PX)})); }
.merch-marquee.mq-vcr .mq-quote.down .mq-arrow::before { content: '\\25BC'; }
.merch-marquee .mq-item.mq-flash-up { animation: mq-flash-up ${FLASH_MS}ms ease-out; }
.merch-marquee .mq-item.mq-flash-down { animation: mq-flash-down ${FLASH_MS}ms ease-out; }
@keyframes mq-flash-up { 0% { background: ${hexToRgba(MERCH_PRICE_UP_COLOR, 0.28)}; } 100% { background: transparent; } }
@keyframes mq-flash-down { 0% { background: ${hexToRgba(MERCH_PRICE_DOWN_COLOR, 0.28)}; } 100% { background: transparent; } }
.merch-marquee .mq-item.mq-blink .mq-quote { animation: mq-blink ${BLINK_CYCLE_MS}ms steps(1, end) ${BLINK_CYCLES}; }
@keyframes mq-blink { 0% { visibility: hidden; } 50% { visibility: visible; } 100% { visibility: visible; } }
.merch-marquee .mq-msg { padding-right: var(--mq-bottom-sepgap, 1.5em); }
.merch-marquee .mq-mlabel.green { color: ${MERCH_PRICE_UP_COLOR}; }
.merch-marquee .mq-mlabel.red { color: ${MERCH_PRICE_DOWN_COLOR}; }
.merch-marquee .mq-mlabel.white { color: var(--mq-text, ${MARQUEE_NEUTRAL_COLOR}); }
`

export type MarqueePulseAnim = 'FLASH' | 'BLINK' | 'TWEEN'
export interface MarqueeFlashEntry {
  dir: 'up' | 'down'
  anim: MarqueePulseAnim
  token: number
  at: number
  cycle: number
}
export type MarqueeFlash = Map<string, MarqueeFlashEntry>
export interface MarqueeRenderFlashEntry {
  dir: 'up' | 'down'
  anim: MarqueePulseAnim
  token: number
  delayMs: number
}
export type MarqueeRenderFlash = Map<string, MarqueeRenderFlashEntry>

const NO_FLASH: MarqueeFlash = new Map()
const NO_RENDER_FLASH: MarqueeRenderFlash = new Map()
const NO_DELTAS: Record<string, number> = {}

interface BarTween {
  handle: MerchTweenHandle
  priceIdx: number
  pctIdx: number
}
type BarTweens = Map<string, BarTween>

interface BarDisplayed { price: number | null, pct: number | null }

interface BarTweenPlan {
  id: string
  fromPrice: number | null
  fromPct: number | null
  fromPctLabel: string | null
  toPrice: number | null
  toPctLabel: string | null
}

export interface MarqueeTweenValues { price: number | null, pct: string | null }

const captureBarDisplayed = (tweens: BarTweens): Map<string, BarDisplayed> => {
  const out = new Map<string, BarDisplayed>()
  for (const [id, t] of tweens) {
    out.set(id, {
      price: t.priceIdx >= 0 ? Math.round(t.handle.values[t.priceIdx]) : null,
      pct: t.pctIdx >= 0 ? t.handle.values[t.pctIdx] : null
    })
  }
  return out
}

const stopBarTweens = (tweens: BarTweens, snap: boolean): void => {
  for (const t of tweens.values()) t.handle.stop(snap)
  tweens.clear()
}

const writeBarQuote = (bar: HTMLElement | null, id: string, price: string | null, pct: string | null, holdPx: number | null): void => {
  if (bar === null) return
  for (const item of bar.querySelectorAll<HTMLElement>('[data-mq-id]')) {
    if (item.getAttribute('data-mq-id') !== id) continue
    if (price !== null) {
      const el = item.querySelector('.mq-price')
      if (el !== null) el.textContent = price
    }
    if (pct !== null) {
      const el = item.querySelector('.mq-pct')
      if (el !== null) el.textContent = pct
    }
    const quote = item.querySelector<HTMLElement>('.mq-quote')
    if (quote !== null) {
      const want = holdPx === null ? '' : `${holdPx}px`
      if (quote.style.minWidth !== want) quote.style.minWidth = want
    }
  }
}

const widthsById = (copy: HTMLElement, descale = 1): Map<string, number> => {
  const out = new Map<string, number>()
  for (const el of copy.querySelectorAll<HTMLElement>('[data-mq-id]')) {
    const id = el.getAttribute('data-mq-id')
    if (id !== null) out.set(id, el.getBoundingClientRect().width / descale)
  }
  return out
}

function MarqueeBar<T> ({
  barClass,
  wrapWidth,
  speedPxS,
  reverse = false,
  naturalWidthPatches = false,
  metricsSig,
  items,
  idOf,
  structureOf,
  contentOf,
  flashHint,
  changeAnimation,
  tweenMs,
  tweenOf,
  renderCopy
}: {
  barClass: string
  wrapWidth: number
  speedPxS: number
  reverse?: boolean
  naturalWidthPatches?: boolean
  metricsSig: string
  items: T[]
  idOf: (item: T) => string
  structureOf: (item: T) => string
  contentOf: (item: T) => string
  flashHint: Record<string, number>
  changeAnimation: MerchChangeAnimation
  tweenMs: number
  tweenOf?: (item: T) => MarqueeTweenValues
  renderCopy: (items: T[], flash: MarqueeRenderFlash) => JSX.Element
}): JSX.Element {
  const [live, setLive] = useState<T[]>(items)
  const [liveSpeed, setLiveSpeed] = useState(speedPxS)
  const [liveReverse, setLiveReverse] = useState(reverse)
  const [cycle, setCycle] = useState(0)
  const [copyW, setCopyW] = useState<number | null>(null)
  const [livePhaseMs, setLivePhaseMs] = useState(0)
  const [pending, setPending] = useState<T[] | null>(null)
  const [flash, setFlash] = useState<MarqueeFlash>(NO_FLASH)

  const stagedRef = useRef(items)
  stagedRef.current = items
  const speedRef = useRef(speedPxS)
  speedRef.current = speedPxS
  const liveRef = useRef(live)
  liveRef.current = live
  const liveSpeedRef = useRef(liveSpeed)
  liveSpeedRef.current = liveSpeed
  const reverseRef = useRef(reverse)
  reverseRef.current = reverse
  const liveReverseRef = useRef(liveReverse)
  liveReverseRef.current = liveReverse
  const copyWRef = useRef(copyW)
  copyWRef.current = copyW
  const cycleRef = useRef(cycle)
  cycleRef.current = cycle
  const commitAtRef = useRef(0)
  const barRef = useRef<HTMLDivElement | null>(null)
  const trackRef = useRef<HTMLDivElement | null>(null)
  const copyRef = useRef<HTMLSpanElement | null>(null)
  const measureRef = useRef<HTMLSpanElement | null>(null)
  const anchorRef = useRef<{ at: number, cycle: number } | null>(null)
  const flashTokenRef = useRef(0)
  const flashTimersRef = useRef(new Set<ReturnType<typeof globalThis.setTimeout>>())
  const tweensRef = useRef<BarTweens>(new Map())
  const tweenIdsRef = useRef<Set<string>>(new Set())
  const displayedRef = useRef<Map<string, BarDisplayed>>(new Map())
  const pendingTweenRef = useRef<BarTweenPlan[] | null>(null)
  const settleReanchorRef = useRef(false)
  const patchedContentRef = useRef(false)
  const releaseRef = useRef<{ els: Set<HTMLElement>, timer: ReturnType<typeof globalThis.setTimeout> } | null>(null)
  const suppressReleaseRef = useRef(false)
  const prePatchQuoteWRef = useRef<Map<string, number> | null>(null)

  const fullSig = (list: T[]): string =>
    JSON.stringify(list.map((item) => [idOf(item), structureOf(item), contentOf(item)]))

  const reKey = (phaseMs: number): void => {
    commitAtRef.current = performance.now()
    setLivePhaseMs(phaseMs)
    setCycle((c) => c + 1)
    setCopyW(null)
  }

  const commitStaged = (phaseMs = 0): void => {
    setLive(stagedRef.current)
    setLiveSpeed(speedRef.current)
    setLiveReverse(reverseRef.current)
    setPending(null)
    reKey(phaseMs)
  }

  const trackPhaseFraction = (): number => {
    const track = trackRef.current
    if (track === null) return 0
    const anim = track.getAnimations().find((a) => (a as CSSAnimation).animationName === 'mq-scroll')
    const p = anim?.effect?.getComputedTiming().progress
    return typeof p === 'number' && Number.isFinite(p) ? Math.min(1, Math.max(0, p)) : 0
  }

  const reAnchor = (curW: number, reason: string): void => {
    const oldW = copyWRef.current ?? curW
    const shiftPx = trackPhaseFraction() * oldW
    const phasePx = curW > 0 ? shiftPx % curW : 0
    const travelPx = reverseRef.current ? curW - phasePx : phasePx
    const phaseMs = speedRef.current > 0 ? (travelPx / speedRef.current) * 1000 : 0
    const now = document.timeline.currentTime
    anchorRef.current = typeof now === 'number' ? { at: now, cycle: cycleRef.current + 1 } : null
    merchDebugTrace({ path: 'marquee-reanchor', bar: barClass, reason, oldW: +oldW.toFixed(2), curW: +curW.toFixed(2), phasePx: +phasePx.toFixed(2), speed: speedRef.current, reverse: reverseRef.current })
    setLiveSpeed(speedRef.current)
    setLiveReverse(reverseRef.current)
    reKey(phaseMs)
  }

  const remeasureReanchor = (reason: string): void => {
    if (releaseRef.current !== null) return
    const bar = barRef.current
    const copy = copyRef.current
    if (bar === null || copy === null || copyWRef.current === null) return
    const barRect = bar.getBoundingClientRect()
    if (barRect.width <= 0) return
    const w = copy.getBoundingClientRect().width / (barRect.width / wrapWidth)
    if (w > 0 && Math.abs(w - copyWRef.current) > REMEASURE_EPSILON_PX) reAnchor(w, reason)
  }

  const contentReanchor = (): void => remeasureReanchor('content')

  const quoteWidthPxOf = (id: string): number | null => {
    const bar = barRef.current
    if (bar === null) return null
    for (const item of bar.querySelectorAll<HTMLElement>('[data-mq-id]')) {
      if (item.getAttribute('data-mq-id') !== id) continue
      const quote = item.querySelector<HTMLElement>('.mq-quote')
      if (quote === null) return null
      const w = Number.parseFloat(globalThis.getComputedStyle(quote).width)
      return Number.isFinite(w) && w > 0 ? w : null
    }
    return null
  }

  const finalizeReleases = (reanchor: boolean): void => {
    const rel = releaseRef.current
    if (rel === null) return
    releaseRef.current = null
    globalThis.clearTimeout(rel.timer)
    for (const el of rel.els) {
      el.style.transition = ''
      el.style.minWidth = ''
    }
    if (!reanchor) return
    for (const t of tweensRef.current.values()) {
      if (!t.handle.finished) {
        settleReanchorRef.current = true
        return
      }
    }
    contentReanchor()
  }

  const beginQuoteRelease = (id: string, targetPx: number): boolean => {
    const bar = barRef.current
    if (bar === null) return false
    const els: HTMLElement[] = []
    for (const item of bar.querySelectorAll<HTMLElement>('[data-mq-id]')) {
      if (item.getAttribute('data-mq-id') !== id) continue
      const quote = item.querySelector<HTMLElement>('.mq-quote')
      if (quote !== null && quote.style.minWidth !== '') els.push(quote)
    }
    if (els.length === 0) return false
    for (const el of els) el.style.transition = `min-width ${WIDTH_RELEASE_MS}ms ease-out`
    void bar.offsetWidth
    for (const el of els) el.style.minWidth = `${targetPx}px`
    const timer = globalThis.setTimeout(() => finalizeReleases(true), WIDTH_RELEASE_TIMER_MS)
    const rel = releaseRef.current
    if (rel === null) {
      releaseRef.current = { els: new Set(els), timer }
    } else {
      globalThis.clearTimeout(rel.timer)
      for (const el of els) rel.els.add(el)
      rel.timer = timer
    }
    merchDebugTrace({ path: 'marquee-release', bar: barClass, id, copies: els.length })
    return true
  }

  const pulse = (hint: Record<string, number>): void => {
    const hits = Object.entries(hint).filter(([, qty]) => qty !== 0)
    if (hits.length === 0) return
    const ids = hits.map(([id]) => id)
    if (changeAnimation === 'NONE') {
      merchDebugTrace({ path: 'marquee-pulse', verdict: 'skipped', reason: 'NONE', bar: barClass, ids })
      return
    }
    if (document.hidden) {
      merchDebugTrace({ path: 'marquee-pulse', verdict: 'skipped', reason: 'hidden', bar: barClass, ids })
      return
    }
    const anim: MarqueePulseAnim =
      changeAnimation === 'BLINK' ? 'BLINK' : changeAnimation === 'TWEEN' ? 'TWEEN' : 'FLASH'
    if (anim === 'TWEEN') tweenIdsRef.current = new Set(ids)
    const token = ++flashTokenRef.current
    const at = performance.now()
    const startCycle = cycleRef.current
    merchDebugTrace({ path: 'marquee-pulse', verdict: 'fired', bar: barClass, anim, token, cycle: startCycle, ids })
    setFlash((prev) => {
      const next: MarqueeFlash = new Map(prev)
      for (const [id, qty] of hits) next.set(id, { dir: qty > 0 ? 'up' : 'down', anim, token, at, cycle: startCycle })
      return next
    })
    const timer = globalThis.setTimeout(() => {
      flashTimersRef.current.delete(timer)
      setFlash((prev) => {
        const next: MarqueeFlash = new Map()
        for (const [id, entry] of prev) if (entry.token !== token) next.set(id, entry)
        return next
      })
    }, anim === 'BLINK' ? BLINK_MS : anim === 'TWEEN' ? tweenMs : FLASH_MS)
    flashTimersRef.current.add(timer)
  }

  const startBarTween = (plan: BarTweenPlan): void => {
    const from: number[] = []
    const to: number[] = []
    let priceIdx = -1
    let pctIdx = -1
    if (plan.fromPrice !== null && plan.toPrice !== null && plan.fromPrice !== plan.toPrice) {
      priceIdx = from.length
      from.push(plan.fromPrice)
      to.push(plan.toPrice)
    }
    const pctFmt = parseMerchPctLabel(plan.toPctLabel)
    if (plan.fromPct !== null && pctFmt !== null && plan.fromPct !== pctFmt.value) {
      pctIdx = from.length
      from.push(plan.fromPct)
      to.push(pctFmt.value)
    }
    if (from.length === 0) return
    const pctBudget = Math.max((plan.fromPctLabel ?? '').length, (plan.toPctLabel ?? '').length)
    let holdPx: number | null = null
    let settledW: number | null = null
    const write = (values: number[], done: boolean): void => {
      const price = priceIdx < 0 || plan.toPrice === null
        ? null
        : `$${merchTweenMoney(done ? plan.toPrice : Math.round(values[priceIdx]))}`
      const pct = pctIdx < 0 || pctFmt === null
        ? null
        : done
          ? (plan.toPctLabel ?? '')
          : formatMerchTweenPct(values[pctIdx], pctFmt, pctBudget, plan.toPctLabel ?? '')
      const slidePx = done && holdPx !== null && settledW !== null ? holdPx - settledW : 0
      const animateRelease = slidePx > WIDTH_RELEASE_MIN_PX && !suppressReleaseRef.current && !document.hidden
      writeBarQuote(barRef.current, plan.id, price, pct, !done || animateRelease ? holdPx : null)
      if (animateRelease && settledW !== null && !beginQuoteRelease(plan.id, settledW)) {
        writeBarQuote(barRef.current, plan.id, null, null, null)
      }
      if (done && settleReanchorRef.current) {
        let allDone = true
        for (const t of tweensRef.current.values()) {
          if (!t.handle.finished) {
            allDone = false
            break
          }
        }
        if (allDone) {
          settleReanchorRef.current = false
          if (releaseRef.current === null) contentReanchor()
        }
      }
    }
    settledW = quoteWidthPxOf(plan.id)
    write(from, false)
    const fromW = quoteWidthPxOf(plan.id)
    if (settledW !== null || fromW !== null) {
      holdPx = Math.max(settledW ?? 0, fromW ?? 0)
      writeBarQuote(barRef.current, plan.id, null, null, holdPx)
    }
    const handle = startMerchTween(from, to, tweenMs, write)
    if (!handle.finished) tweensRef.current.set(plan.id, { handle, priceIdx, pctIdx })
  }

  useLayoutEffect(() => {
    const staged = items
    const current = liveRef.current
    suppressReleaseRef.current = true
    finalizeReleases(true)
    displayedRef.current = captureBarDisplayed(tweensRef.current)
    stopBarTweens(tweensRef.current, true)
    suppressReleaseRef.current = false
    tweenIdsRef.current = new Set()
    if (current.length === 0) {
      if (staged.length > 0) commitStaged()
      pulse(flashHint)
      return
    }
    if (fullSig(staged) !== fullSig(current)) {
      const byId = new Map(staged.map((item) => [idOf(item), item]))
      let patched = false
      const hybrid = current.map((old) => {
        const next = byId.get(idOf(old))
        if (next === undefined) return old
        if (structureOf(next) !== structureOf(old)) return old
        if (contentOf(next) === contentOf(old)) return old
        patched = true
        return next
      })
      if (patched) setPending(hybrid)
    }
    pulse(flashHint)
  }, [items])

  useLayoutEffect(() => {
    if (pending === null) return
    const measure = measureRef.current
    const copy = copyRef.current
    const current = liveRef.current
    const tweenIds = tweenIdsRef.current
    tweenIdsRef.current = new Set()
    const displayed = displayedRef.current
    displayedRef.current = new Map()
    const plan: BarTweenPlan[] = []
    const mint = (old: T, item: T): void => {
      const id = idOf(item)
      if (!tweenIds.has(id) || tweenOf === undefined) return
      const before = tweenOf(old)
      const after = tweenOf(item)
      const wasRolling = displayed.get(id)
      const beforePct = parseMerchPctLabel(before.pct)
      plan.push({
        id,
        fromPrice: wasRolling?.price ?? before.price,
        fromPct: wasRolling?.pct ?? (beforePct === null ? null : beforePct.value),
        fromPctLabel: before.pct,
        toPrice: after.price,
        toPctLabel: after.pct
      })
    }
    let next = current
    if (naturalWidthPatches) {
      const preW = new Map<string, number>()
      next = pending.map((item, i) => {
        const old = current[i]
        if (item === old) return old
        const w = quoteWidthPxOf(idOf(item))
        if (w !== null) preW.set(idOf(item), w)
        mint(old, item)
        return item
      })
      prePatchQuoteWRef.current = preW.size > 0 ? preW : null
    } else if (measure !== null && copy !== null) {
      const barEl = barRef.current
      const barW = barEl === null ? 0 : barEl.getBoundingClientRect().width
      const descale = barW > 0 ? barW / wrapWidth : 1
      const liveW = widthsById(copy, descale)
      const newW = widthsById(measure, descale)
      next = pending.map((item, i) => {
        const old = current[i]
        if (item === old) return old
        const id = idOf(item)
        const a = liveW.get(id)
        const b = newW.get(id)
        if (a === undefined || b === undefined || Math.abs(a - b) > PATCH_WIDTH_EPSILON_PX) return old
        mint(old, item)
        return item
      })
    }
    setPending(null)
    if (next.some((item, i) => item !== current[i])) {
      if (plan.length > 0) pendingTweenRef.current = plan
      patchedContentRef.current = true
      setLive(next)
    }
  }, [pending])

  useLayoutEffect(() => {
    const plan = pendingTweenRef.current
    pendingTweenRef.current = null
    const preW = prePatchQuoteWRef.current
    prePatchQuoteWRef.current = null
    if (naturalWidthPatches && patchedContentRef.current) {
      let released = false
      if (preW !== null && !document.hidden) {
        const rolled = new Set((plan ?? []).map((p) => p.id))
        for (const [id, oldW] of preW) {
          if (rolled.has(id)) continue
          const newW = quoteWidthPxOf(id)
          if (newW === null || oldW - newW <= WIDTH_RELEASE_MIN_PX) continue
          writeBarQuote(barRef.current, id, null, null, oldW)
          if (beginQuoteRelease(id, newW)) released = true
          else writeBarQuote(barRef.current, id, null, null, null)
        }
      }
      if (plan === null) {
        if (!released) contentReanchor()
      } else {
        settleReanchorRef.current = true
      }
    }
    patchedContentRef.current = false
    if (plan === null) return
    for (const entry of plan) startBarTween(entry)
  }, [live])

  const metricsSigRef = useRef(metricsSig)
  useLayoutEffect(() => {
    const speedStale = speedPxS !== liveSpeedRef.current
    const metricsStale = metricsSigRef.current !== metricsSig
    const reverseStale = reverse !== liveReverseRef.current
    metricsSigRef.current = metricsSig
    if (!speedStale && !metricsStale && !reverseStale) return
    if (liveRef.current.length === 0 || copyWRef.current === null) {
      if (speedStale) setLiveSpeed(speedPxS)
      if (reverseStale) setLiveReverse(reverse)
      return
    }
    finalizeReleases(false)
    let curW = copyWRef.current
    const bar = barRef.current
    const copy = copyRef.current
    if (bar !== null && copy !== null) {
      const barRect = bar.getBoundingClientRect()
      if (barRect.width > 0) {
        const w = copy.getBoundingClientRect().width / (barRect.width / wrapWidth)
        if (w > 0) curW = w
      }
    }
    if (!speedStale && !reverseStale && Math.abs(curW - copyWRef.current) <= REMEASURE_EPSILON_PX) return
    reAnchor(curW, speedStale ? 'speed' : reverseStale ? 'reverse' : 'metrics')
  }, [speedPxS, reverse, metricsSig])

  useEffect(() => {
    const onVisibilityReturn = (): void => {
      if (document.hidden) return
      for (const timer of flashTimersRef.current) globalThis.clearTimeout(timer)
      flashTimersRef.current.clear()
      suppressReleaseRef.current = true
      finalizeReleases(true)
      stopBarTweens(tweensRef.current, true)
      suppressReleaseRef.current = false
      setFlash((prev) => (prev.size === 0 ? prev : NO_FLASH))
    }
    document.addEventListener('visibilitychange', onVisibilityReturn)
    const timers = flashTimersRef.current
    const tweens = tweensRef.current
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityReturn)
      for (const timer of timers) globalThis.clearTimeout(timer)
      timers.clear()
      suppressReleaseRef.current = true
      finalizeReleases(false)
      stopBarTweens(tweens, true)
      suppressReleaseRef.current = false
    }
  }, [])

  useLayoutEffect(() => {
    if (copyWRef.current !== null || liveRef.current.length === 0) return
    const bar = barRef.current
    const copy = copyRef.current
    if (bar === null || copy === null) return
    const barRect = bar.getBoundingClientRect()
    if (barRect.width <= 0) return
    const w = copy.getBoundingClientRect().width / (barRect.width / wrapWidth)
    if (w > 0) setCopyW(w)
  }, [live, cycle, wrapWidth])

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return
    const copy = copyRef.current
    if (copy === null) return
    const ro = new ResizeObserver(() => remeasureReanchor('resize'))
    ro.observe(copy)
    return () => ro.disconnect()
  }, [cycle, live])

  useLayoutEffect(() => {
    const anchor = anchorRef.current
    if (anchor === null || copyW === null) return
    anchorRef.current = null
    if (anchor.cycle !== cycle) return
    const track = trackRef.current
    const anim = track?.getAnimations().find((a) => (a as CSSAnimation).animationName === 'mq-scroll')
    if (anim !== undefined) anim.startTime = anchor.at
  }, [copyW, cycle])

  const seamPhaseMs = (track: HTMLDivElement, e: React.AnimationEvent<HTMLDivElement>): number => {
    const anim = track.getAnimations().find((a) => (a as CSSAnimation).animationName === 'mq-scroll')
    const now = document.timeline.currentTime
    if (anim === undefined || anim.startTime === null || typeof now !== 'number') return 0
    const start = typeof anim.startTime === 'number' ? anim.startTime : Number(anim.startTime)
    const delay = anim.effect?.getComputedTiming().delay ?? 0
    const phase = now - (start + delay + e.elapsedTime * 1000)
    return Number.isFinite(phase) ? Math.min(MAX_SEAM_PHASE_MS, Math.max(0, phase)) : 0
  }

  const onIteration = (e: React.AnimationEvent<HTMLDivElement>): void => {
    if (e.animationName !== 'mq-scroll') return
    const track = e.currentTarget
    finalizeReleases(false)
    if (fullSig(stagedRef.current) !== fullSig(liveRef.current) || speedRef.current !== liveSpeedRef.current || reverseRef.current !== liveReverseRef.current) {
      const phase = seamPhaseMs(track, e)
      flushSync(() => commitStaged(phase))
      return
    }
    const bar = barRef.current
    const copy = copyRef.current
    if (bar === null || copy === null || copyWRef.current === null) return
    const barRect = bar.getBoundingClientRect()
    if (barRect.width <= 0) return
    const w = copy.getBoundingClientRect().width / (barRect.width / wrapWidth)
    if (w > 0 && Math.abs(w - copyWRef.current) > REMEASURE_EPSILON_PX) {
      const phase = seamPhaseMs(track, e)
      flushSync(() => reKey(phase))
    }
  }

  let renderFlash: MarqueeRenderFlash = NO_RENDER_FLASH
  if (flash.size > 0) {
    renderFlash = new Map()
    for (const [id, entry] of flash) {
      renderFlash.set(id, {
        dir: entry.dir,
        anim: entry.anim,
        token: entry.token,
        delayMs: entry.cycle === cycle ? 0 : -Math.max(0, Math.round(commitAtRef.current - entry.at))
      })
    }
  }

  const copies = copyW === null ? 1 : Math.max(2, Math.ceil(wrapWidth / copyW) + 1)
  const children: JSX.Element[] = []
  for (let i = 0; i < copies; i++) {
    children.push(
      <span
        key={i}
        className='mq-copy'
        ref={i === 0 ? copyRef : undefined}
        aria-hidden={i === 0 ? undefined : true}
      >
        {renderCopy(live, renderFlash)}
      </span>
    )
  }
  const trackVars: Record<string, string> = copyW === null
    ? {}
    : { '--mq-shift': `${-copyW}px`, '--mq-dur': `${copyW / liveSpeed}s`, '--mq-delay': `${-livePhaseMs}ms` }
  return (
    <div ref={barRef} className={`mq-bar ${barClass}`}>
      {live.length > 0 && (
        <div
          key={cycle}
          ref={trackRef}
          className={copyW === null
            ? 'mq-track'
            : liveReverse ? 'mq-track mq-anim mq-rev' : 'mq-track mq-anim'}
          style={trackVars as React.CSSProperties}
          onAnimationIteration={onIteration}
        >
          {children}
        </div>
      )}
      {pending !== null && (
        <span ref={measureRef} className='mq-copy mq-measure' aria-hidden='true'>
          {renderCopy(pending, renderFlash)}
        </span>
      )}
    </div>
  )
}

const productId = (p: MerchTickerMarqueeProduct): string => p.productId
const productStructure = (p: MerchTickerMarqueeProduct): string =>
  JSON.stringify([p.title, p.priceCents === null])
const productContent = (p: MerchTickerMarqueeProduct): string =>
  JSON.stringify([p.priceCents, p.trend, p.pctLabel])

const messageId = (m: MerchTickerEnvelopeMessage): string => m.id
const messageStructure = (): string => ''
const messageContent = (m: MerchTickerEnvelopeMessage): string =>
  JSON.stringify([m.label, m.labelColor, m.text])

const productTweenValues = (p: MerchTickerMarqueeProduct): MarqueeTweenValues => ({
  price: p.priceCents,
  pct: p.pctLabel
})

const renderTopCopy = (products: MerchTickerMarqueeProduct[], flash: MarqueeRenderFlash): JSX.Element => (
  <>
    {products.map((p) => {
      const dir = p.trend === 'up' ? 'up' : p.trend === 'down' ? 'down' : 'flat'
      const pulse = flash.get(p.productId)
      let itemClass = 'mq-item'
      if (pulse !== undefined && pulse.anim !== 'TWEEN') {
        itemClass = pulse.anim === 'BLINK' ? 'mq-item mq-blink' : `mq-item mq-flash-${pulse.dir}`
      }
      const itemStyle = pulse !== undefined && pulse.anim !== 'TWEEN' && pulse.delayMs < 0
        ? { animationDelay: `${pulse.delayMs}ms` }
        : undefined
      const pct = p.pctLabel
      return (
        <React.Fragment key={pulse === undefined || pulse.anim === 'TWEEN' ? p.productId : `${p.productId}:${pulse.token}`}>
          <span className={itemClass} style={itemStyle} data-mq-id={p.productId}>
            <span className='mq-name'>{p.title}</span>
            {p.priceCents !== null && (
              <>
                {' '}
                <span className={`mq-quote ${dir}`} style={itemStyle}>
                  <span className='mq-arrow' aria-hidden='true' />
                  {' '}
                  <span className='mq-qslot'>
                    <span className='mq-price'>{`$${(p.priceCents / 100).toFixed(2)}`}</span>
                    {' '}
                    <span className='mq-pct'>{pct !== null && pct !== '' ? pct : ''}</span>
                  </span>
                </span>
              </>
            )}
          </span>
          <span className='mq-div'>&#x2F;&#x2F;</span>
        </React.Fragment>
      )
    })}
  </>
)

const MESSAGE_LABEL_CLASS: Record<string, string> = {
  green: 'mq-mlabel green',
  red: 'mq-mlabel red',
  white: 'mq-mlabel white'
}

const renderBottomCopy = (messages: MerchTickerEnvelopeMessage[]): JSX.Element => (
  <>
    {messages.map((m) => (
      <span key={m.id} className='mq-msg' data-mq-id={m.id}>
        {m.labelColor === 'hide'
          ? m.text
          : (
            <>
              <span className={MESSAGE_LABEL_CLASS[m.labelColor] ?? MESSAGE_LABEL_CLASS.green}>{m.label}</span>
              <span className='mq-div'>&#x2F;&#x2F;</span>
              {m.text}
            </>
            )}
      </span>
    ))}
  </>
)

const SLOT_DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']

const slotPriceText = (cents: number): string => `$${(cents / 100).toFixed(2)}`

const priceSlotCandidates = (minCents: number | null, maxCents: number): string[] => {
  const lo = Math.min(minCents ?? 1, maxCents)
  const out = [slotPriceText(lo), slotPriceText(maxCents)]
  const intDigits = Math.max(1, String(Math.floor(maxCents / 100)).length)
  for (const d of SLOT_DIGITS) out.push(`$${d.repeat(intDigits)}.${d}${d}`)
  return [...new Set(out)]
}

const pctSlotCandidates = (maxPct: number): string[] => {
  const mag = Math.abs(maxPct)
  const label = mag.toFixed(1)
  const out = [`+${label}%`, `-${label}%`, '0.0%']
  const intDigits = Math.max(1, String(Math.trunc(mag)).length)
  for (const d of SLOT_DIGITS) {
    out.push(`+${d.repeat(intDigits)}.${d}%`)
    out.push(`-${d.repeat(intDigits)}.${d}%`)
  }
  return [...new Set(out)]
}

export const MerchTickerMarquee = ({
  envelope,
  wrapWidth
}: {
  envelope: MerchTickerMarqueeEnvelope
  wrapWidth?: number
}): JSX.Element => {
  const w = wrapWidth ?? MERCH_TICKER_MARQUEE_WIDTH
  const ts = envelope.tickerStyle
  const speed = Number.isFinite(ts.scrollSpeedPxS) && ts.scrollSpeedPxS > 0
    ? ts.scrollSpeedPxS
    : MARQUEE_SPEED_PX_S
  const pinnedOrderRef = useRef<string[]>([])
  const products = useMemo(() => {
    const pin = pinnedOrderRef.current
    for (const p of envelope.products) {
      if (!pin.includes(p.productId)) pin.push(p.productId)
    }
    const byId = new Map(envelope.products.map((p) => [p.productId, p]))
    const pinned: MerchTickerMarqueeProduct[] = []
    for (const id of pin) {
      const p = byId.get(id)
      if (p !== undefined) pinned.push(p)
    }
    return pinned
  }, [envelope.products])
  const [fontEpoch, setFontEpoch] = useState(0)
  useLayoutEffect(() => {
    if (typeof document === 'undefined' || document.fonts === undefined) return
    const fonts = document.fonts
    const bump = (): void => setFontEpoch((e) => e + 1)
    fonts.addEventListener('loadingdone', bump)
    let disposed = false
    void fonts.ready.then(() => {
      if (!disposed) bump()
    })
    return () => {
      disposed = true
      fonts.removeEventListener('loadingdone', bump)
    }
  }, [])
  const priceSlotOn = ts.priceSlotMaxCents != null
  const pctSlotOn = ts.pctSlotMaxPct != null
  const [slotPx, setSlotPx] = useState<{ price: number | null, pct: number | null }>({ price: null, pct: null })
  const [slotOverflow, setSlotOverflow] = useState('')
  const slotGrowRef = useRef({ sig: '', price: 0, pct: 0 })
  const slotMeasureRef = useRef<HTMLDivElement | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const slotCfgSig = `${ts.priceSlotMinCents ?? ''}:${ts.priceSlotMaxCents ?? ''}:${ts.pctSlotMaxPct ?? ''}:${ts.fontKey}:${ts.topTextSizePx}:${fontEpoch}`
  const priceCandidates = useMemo(() => {
    const max = ts.priceSlotMaxCents
    return max == null ? [] : priceSlotCandidates(ts.priceSlotMinCents, max)
  }, [ts.priceSlotMinCents, ts.priceSlotMaxCents])
  const pctCandidates = useMemo(() => {
    const max = ts.pctSlotMaxPct
    return max == null ? [] : pctSlotCandidates(max)
  }, [ts.pctSlotMaxPct])
  const livePriceTexts = useMemo(() => {
    if (ts.priceSlotMaxCents == null) return []
    const texts = new Set<string>()
    for (const p of products) {
      if (p.priceCents !== null) texts.add(slotPriceText(p.priceCents))
    }
    return [...texts]
  }, [ts.priceSlotMaxCents, products])
  const livePctTexts = useMemo(() => {
    if (ts.pctSlotMaxPct == null) return []
    const texts = new Set<string>()
    for (const p of products) {
      if (p.pctLabel !== null && p.pctLabel !== '') texts.add(p.pctLabel)
    }
    return [...texts]
  }, [ts.pctSlotMaxPct, products])
  useLayoutEffect(() => {
    const grow = slotGrowRef.current
    if (grow.sig !== slotCfgSig) {
      grow.sig = slotCfgSig
      grow.price = 0
      grow.pct = 0
    }
    if (!priceSlotOn && !pctSlotOn) {
      setSlotPx((prev) => (prev.price === null && prev.pct === null ? prev : { price: null, pct: null }))
      setSlotOverflow((prev) => (prev === '' ? prev : ''))
      return
    }
    const probe = slotMeasureRef.current
    const root = rootRef.current
    if (probe === null || root === null) return
    const rootW = root.getBoundingClientRect().width
    const descale = rootW > 0 ? rootW / w : 1
    const groupMax = (group: string): number => {
      let widest = 0
      for (const el of probe.querySelectorAll<HTMLElement>(`[data-slot-group="${group}"]`)) {
        const px = el.getBoundingClientRect().width / descale
        if (px > widest) widest = px
      }
      return widest
    }
    let over = ''
    let price: number | null = null
    if (priceSlotOn) {
      const base = groupMax('price')
      const live = groupMax('price-live')
      if (live > base + REMEASURE_EPSILON_PX && live > grow.price) {
        grow.price = live
        merchDebugTrace({ path: 'marquee-slot-overflow', slot: 'price', basePx: +base.toFixed(2), livePx: +live.toFixed(2) })
      }
      price = Math.ceil(Math.max(base, grow.price) * 100) / 100
      if (grow.price > base + REMEASURE_EPSILON_PX) over = 'price'
    }
    let pct: number | null = null
    if (pctSlotOn) {
      const base = groupMax('pct')
      const live = groupMax('pct-live')
      if (live > base + REMEASURE_EPSILON_PX && live > grow.pct) {
        grow.pct = live
        merchDebugTrace({ path: 'marquee-slot-overflow', slot: 'pct', basePx: +base.toFixed(2), livePx: +live.toFixed(2) })
      }
      pct = Math.ceil(Math.max(base, grow.pct) * 100) / 100
      if (grow.pct > base + REMEASURE_EPSILON_PX) over = over === '' ? 'pct' : `${over} pct`
    }
    if (price !== null && price <= 0) price = null
    if (pct !== null && pct <= 0) pct = null
    setSlotPx((prev) => (prev.price === price && prev.pct === pct ? prev : { price, pct }))
    setSlotOverflow((prev) => (prev === over ? prev : over))
  }, [slotCfgSig, products])
  const topMetricsSig = `${ts.fontKey}:${fontEpoch}:${ts.topTextSizePx}:${ts.topSeparatorGapPx ?? 'auto'}:${slotPx.price ?? 'off'}:${slotPx.pct ?? 'off'}`
  const bottomMetricsSig = `${ts.fontKey}:${fontEpoch}:${ts.bottomTextSizePx}:${ts.bottomSeparatorGapPx ?? 'auto'}`
  const rootVars: Record<string, string> = {
    '--mq-wrap': `${w}px`,
    '--mq-font': marqueeFontStack(ts.fontKey),
    '--mq-bottom-bg': hexToRgba(ts.bottomBgColor, ts.bottomOpacity),
    '--mq-pad-top': `${ts.bottomPadTop}px`,
    '--mq-pad-bottom': `${ts.bottomPadBottom}px`,
    '--mq-text': ts.textColor,
    '--mq-top-bg': ts.topBgColor,
    '--mq-top-pad-top': `${ts.topPadTop}px`,
    '--mq-top-pad-bottom': `${ts.topPadBottom}px`,
    '--mq-border-color': ts.borderColor,
    '--mq-border-w': `${ts.borderWidth}px`,
    '--mq-gap-h': `${ts.gapHeight}px`,
    '--mq-gap-bg': ts.gapColor,
    '--mq-top-outer-pad': `${ts.topOuterPad}px`,
    '--mq-bottom-outer-pad': `${ts.bottomOuterPad}px`,
    '--mq-top-font': `${ts.topTextSizePx}px`,
    '--mq-top-lh': `${ts.topLineHeightPx}px`,
    '--mq-bottom-font': `${ts.bottomTextSizePx}px`,
    '--mq-bottom-lh': `${ts.bottomLineHeightPx}px`
  }
  if (ts.textGlowPx > 0) {
    rootVars['--mq-text-glow'] = `0 0 ${ts.textGlowPx}px ${ts.textGlowColor}`
  }
  if (ts.textGlowPx > 0 && ts.textGlowArrows) {
    rootVars['--mq-arrow-glow'] = `drop-shadow(0 0 ${ts.textGlowPx}px ${ts.textGlowColor})`
  }
  if (ts.topSeparatorGapPx != null) {
    rootVars['--mq-top-sepgap'] = `${ts.topSeparatorGapPx}px`
  }
  if (ts.bottomSeparatorGapPx != null) {
    rootVars['--mq-bottom-sepgap'] = `${ts.bottomSeparatorGapPx}px`
  }
  if (slotPx.price !== null) {
    rootVars['--mq-price-slot'] = `${slotPx.price}px`
  }
  if (slotPx.pct !== null) {
    rootVars['--mq-pct-slot'] = `${slotPx.pct}px`
  }
  return (
    <div
      ref={rootRef}
      className={[
        'merch-marquee',
        ts.fontKey === 'VCR_OSD_MONO' ? 'mq-vcr' : '',
        ts.textGlowPx > 0 && !ts.textGlowArrows ? 'mq-noarrowglow' : '',
        slotPx.price !== null ? 'mq-priceslot' : '',
        slotPx.pct !== null ? 'mq-pctslot' : ''
      ].filter((c) => c !== '').join(' ')}
      style={rootVars as React.CSSProperties}
      data-testid='merch-ticker-marquee'
      {...(slotOverflow !== '' ? { 'data-mq-slot-overflow': slotOverflow } : {})}
    >
      <style>{MARQUEE_CSS}</style>
      {(priceSlotOn || pctSlotOn) && (
        <div ref={slotMeasureRef} className='mq-slotmeasure' aria-hidden='true'>
          {priceCandidates.map((text) => (
            <span key={`c$${text}`} data-slot-group='price'>{text}</span>
          ))}
          {livePriceTexts.map((text) => (
            <span key={`l$${text}`} data-slot-group='price-live'>{text}</span>
          ))}
          {pctCandidates.map((text) => (
            <span key={`c%${text}`} data-slot-group='pct'>{text}</span>
          ))}
          {livePctTexts.map((text) => (
            <span key={`l%${text}`} data-slot-group='pct-live'>{text}</span>
          ))}
        </div>
      )}
      <div className='mq-outer-top' />
      <MarqueeBar
        barClass='mq-top'
        wrapWidth={w}
        speedPxS={speed * TOP_SPEED_MULT}
        naturalWidthPatches
        metricsSig={topMetricsSig}
        items={products}
        idOf={productId}
        structureOf={productStructure}
        contentOf={productContent}
        flashHint={envelope.deltas}
        changeAnimation={ts.changeAnimation}
        tweenMs={ts.tweenMs}
        tweenOf={productTweenValues}
        renderCopy={renderTopCopy}
      />
      <div className='mq-gap' />
      <MarqueeBar
        barClass='mq-bottom'
        wrapWidth={w}
        speedPxS={speed * BOTTOM_SPEED_MULT}
        reverse={ts.bottomScrollReverse}
        metricsSig={bottomMetricsSig}
        items={envelope.messages}
        idOf={messageId}
        structureOf={messageStructure}
        contentOf={messageContent}
        flashHint={NO_DELTAS}
        changeAnimation={ts.changeAnimation}
        tweenMs={ts.tweenMs}
        renderCopy={renderBottomCopy}
      />
      <div className='mq-outer-bottom' />
    </div>
  )
}
